// Helpers for error handling when using React Query
import * as _ from 'lodash';

import type { BaseFossaError } from '../types';

//  This is helpful for when the backend throws something which extends BaseFossaError
//  An example of this would be a BadRequestError
export type ErrorResponse = Response & { data: BaseFossaError };

/**
 * Helper for determining if an error is an ErrorResponse
 *
 * @param {unknown} error - The error to check
 * @returns {boolean} Whether the error is an ErrorResponse
 */
export function isErrorResponse(error: unknown): error is ErrorResponse {
  const isResponse = error instanceof Response;
  if (!isResponse) {
    return false;
  }

  if (!('data' in error)) {
    return false;
  }

  const isBaseFossaError = _.isString((error.data as BaseFossaError)?.message);
  if (!isBaseFossaError) {
    return false;
  }

  return true;
}

/**
 * Helper for getting the error message from an error response,
 * or from an error object.
 *
 * @param {unknown} error - The error to get the message from
 * @param {string} [fallbackMessage] - The fallback message to use if the error is not an ErrorResponse or an error object @default 'An unknown error occurred'
 * @returns {string} The error message
 */
export function getErrorMessage(error: unknown, fallbackMessage: string = 'An unknown error occurred'): string {
  if (isErrorResponse(error)) {
    return error.data.message;
  }

  if (_.isError(error)) {
    return error.message;
  }

  return fallbackMessage;
}
